import { UserStore } from '../../engine/streamrapp/UserStore';
import { StreamrAppService } from '../streamrapp/StreamrAppService';
import { UserService } from '../user/UserService';
import { AcceptedParticipantType, UserRole } from '../user/UserServiceOutput';
import { LobbyServiceOutput } from './LobbyServiceOutput';
const METADATA_TIMEOUT_MS = 5000;
export class LobbyService {
    static instance;
    output;
    activeBroadcast;
    dismissed = false;
    autoJoinTried = false;
    metadataTimeoutId;
    constructor() {
        this.output = new LobbyServiceOutput();
    }
    static getInstance() {
        if (!LobbyService.instance) {
            LobbyService.instance = new LobbyService();
        }
        return LobbyService.instance;
    }
    static getNotificationInterface() {
        return LobbyService.getInstance();
    }
    static getOutput() {
        return LobbyServiceOutput.getReactiveOutput();
    }
    /**
     * Called when navigating to a broadcast page.
     * Idempotent per broadcast — safe to call during React render.
     */
    notifyEnterLobby(activeBroadcast) {
        if (this.activeBroadcast?.streamId === activeBroadcast.streamId) {
            return;
        }
        this.activeBroadcast = activeBroadcast;
        this.dismissed = false;
        this.autoJoinTried = false;
        this.clearMetadataTimeout();
        const isBroadcaster = activeBroadcast.myRole === 'broadcaster';
        const hasDisplayName = !!new UserStore().getDisplayName();
        if (isBroadcaster) {
            // The engine reports the role (COHOST) only once its UserManager
            // is up, which takes seconds — until then the UI would treat the
            // host as a VIEWER and flash viewer chrome such as the
            // "Request to speak" button. The role is already known here.
            UserService.getNotificationInterface().notifySetUserRole(UserRole.COHOST);
            if (hasDisplayName) {
                this.dismissed = true;
                this.output.setLobbyView('none');
            }
            else {
                this.output.setLobbyView('name-entry');
            }
            StreamrAppService.getInstance().notifyPrepareRendering(activeBroadcast);
            // Host reload recovery: if this session was live when the page
            // was refreshed, resume the broadcast automatically instead of
            // waiting for another Go Live click.
            if (hasDisplayName &&
                new UserStore().getHostWasLive(activeBroadcast.streamId)) {
                console.log('[LobbyService] session was live — resuming broadcast after reload');
                StreamrAppService.getInstance().notifyActivateBroadcast(activeBroadcast);
            }
        }
        else {
            this.output.setLobbyView('connecting');
            StreamrAppService.getInstance().notifyActivateBroadcast(activeBroadcast);
        }
    }
    /**
     * Called by StreamrAppService when the engine is ready.
     * Starts the metadata timeout for viewers.
     */
    notifyEngineReady() {
        if (this.dismissed)
            return;
        if (!this.activeBroadcast)
            return;
        if (this.activeBroadcast.myRole === 'broadcaster')
            return;
        this.clearMetadataTimeout();
        this.metadataTimeoutId = setTimeout(() => {
            this.metadataTimeoutId = undefined;
            if (!this.dismissed) {
                this.dismissViewer();
            }
        }, METADATA_TIMEOUT_MS);
    }
    /**
     * Called by StreamrAppService when broadcast metadata is received.
     * For viewers: auto-dismisses unless the broadcast is in speakers mode.
     */
    notifyBroadcastMetadataReceived(acceptedParticipantType) {
        // The auto-join link asks for its role once the host is known to be
        // online, whether or not the metadata timeout already let this
        // participant in as a viewer (with late metadata the lobby was
        // dismissed first and the link silently stayed a viewer).
        if (this.tryAutoJoin()) {
            this.clearMetadataTimeout();
            return;
        }
        if (this.dismissed)
            return;
        this.clearMetadataTimeout();
        if (acceptedParticipantType === AcceptedParticipantType.SPEAKERS) {
            const persistedRole = this.activeBroadcast
                ? new UserStore().getUserRole(this.activeBroadcast.broadcasterUserId)
                : undefined;
            if (persistedRole !== undefined &&
                persistedRole !== UserRole.VIEWER) {
                this.dismissed = true;
                this.output.setLobbyView('none');
                return;
            }
            this.output.setLobbyView('request-to-speak');
        }
        else {
            this.dismissViewer();
        }
    }
    /**
     * Called when the user enters their name and confirms.
     */
    notifyNameEntered(name) {
        if (!this.activeBroadcast)
            return;
        StreamrAppService.getNotificationInterface().notifySetDisplayName(name);
        if (this.activeBroadcast.myRole === 'broadcaster') {
            this.dismissed = true;
            this.output.setLobbyView('none');
        }
        else {
            this.output.setLobbyView('connecting');
            StreamrAppService.getInstance().notifyActivateBroadcast(this.activeBroadcast);
        }
    }
    /**
     * Called when a viewer is promoted to speaker or guest.
     */
    notifyPromotedFromViewer() {
        if (!this.activeBroadcast)
            return;
        this.clearMetadataTimeout();
        StreamrAppService.getInstance().notifyPrepareRendering(this.activeBroadcast);
        this.dismissed = true;
        this.output.setLobbyView('none');
    }
    isDismissed() {
        return this.dismissed;
    }
    tryAutoJoin() {
        if (this.autoJoinTried)
            return false;
        const params = new URLSearchParams(window.location.search);
        if (params.get('autoJoin') !== 'true') {
            return false;
        }
        this.autoJoinTried = true;
        const displayName = params.get('displayName') || 'Auto';
        const roleParam = params.get('autoJoinRole');
        const role = roleParam === 'guest' ? UserRole.GUEST : UserRole.SPEAKER;
        StreamrAppService.getNotificationInterface().notifySetDisplayName(displayName);
        UserService.getNotificationInterface().notifyRequestPermission(role);
        // a viewer already watching keeps watching while the host answers
        if (!this.dismissed)
            this.output.setLobbyView('connecting');
        return true;
    }
    dismissViewer() {
        if (this.dismissed)
            return;
        this.dismissed = true;
        this.clearMetadataTimeout();
        StreamrAppService.getNotificationInterface().notifyViewerLobbyDismissed();
        this.output.setLobbyView('none');
    }
    clearMetadataTimeout() {
        if (this.metadataTimeoutId !== undefined) {
            clearTimeout(this.metadataTimeoutId);
            this.metadataTimeoutId = undefined;
        }
    }
}
